import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hasReadState } from '../../components/announcements/announcementUtils';
import systemAnnouncementsApi from '../../utils/api/systemAnnouncementsApi';
import { announcementKeys } from './queryKeys';

const POLL_INTERVAL_MS = 2 * 60 * 1000;

export function useActiveAnnouncements(options = {}) {
  return useQuery({
    queryKey: announcementKeys.active(),
    queryFn: () => systemAnnouncementsApi.getActiveAnnouncements(),
    refetchInterval: POLL_INTERVAL_MS,
    retry: false,
    ...options,
  });
}

export function useAnnouncementArchive(options = {}) {
  return useQuery({
    queryKey: announcementKeys.archive(),
    queryFn: () => systemAnnouncementsApi.getAnnouncementArchive(),
    retry: false,
    ...options,
  });
}

export function useDismissAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (referenceId) => systemAnnouncementsApi.dismissAnnouncement(referenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useMarkAnnouncementRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (referenceId) => systemAnnouncementsApi.markAnnouncementRead(referenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

/** Unread published announcements for sidebar badge (requires API read state). */
export function useUnreadAnnouncementCount(options = {}) {
  const { data: announcements = [], ...rest } = useAnnouncementArchive({
    refetchInterval: POLL_INTERVAL_MS,
    ...options,
  });

  const unreadCount = useMemo(() => {
    return announcements.filter((announcement) => {
      if (announcement.isArchived) return false;
      if (!hasReadState(announcement)) return false;
      return !announcement.isRead;
    }).length;
  }, [announcements]);

  return { unreadCount, hasUnread: unreadCount > 0, ...rest };
}

/** Active high-priority announcements suitable for the global banner. */
export function useBannerAnnouncements() {
  const { data: announcements = [], ...rest } = useActiveAnnouncements();
  const bannerAnnouncements = announcements.filter(
    (a) =>
      (a.priority || '').toLowerCase() === 'high' &&
      !a.isDismissed &&
      !a.dismissedAt
  );
  return { announcements: bannerAnnouncements, ...rest };
}
